// OGC-531 Creatives Manager platform catalog API (docs/SAAS-V1-CURATED-AD-CATALOG.md).
// Every mutation carries an Idempotency-Key; callers keep one key per
// attempt so a retried request replays instead of acting twice.
import { api } from '../../api.js';

export const CATALOG_ROOT = '/api/saas/platform/catalog';

export function makeUuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const raw = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${raw.slice(0, 8)}-${raw.slice(8, 12)}-${raw.slice(12, 16)}-${raw.slice(16, 20)}-${raw.slice(20)}`;
}

export function attemptKey(operation, id) {
  return `catalog-${operation}-${id}-${makeUuid()}`;
}

const withKey = (key) => ({ headers: { 'Idempotency-Key': key } });
const enc = encodeURIComponent;

export function createCreativesApi(client = api) {
  return {
    directory(query = {}) {
      const params = new URLSearchParams(Object.entries(query).filter(([, value]) => value !== '' && value != null));
      return client.get(`${CATALOG_ROOT}/directory?${params}`);
    },
    async entry(packageId) {
      const response = await client.get(`${CATALOG_ROOT}?packageId=${enc(packageId)}`);
      const catalog = Array.isArray(response?.catalog) ? response.catalog : [];
      return catalog.find((entry) => entry?.package?.id === packageId) || null;
    },
    async packageUsage(packageId) {
      return (await client.get(`${CATALOG_ROOT}/packages/${enc(packageId)}/usage`))?.usage || null;
    },
    async signedUrl(versionId, assetKind) {
      return (await client.get(`${CATALOG_ROOT}/versions/${enc(versionId)}/assets/${assetKind}/signed-url`))?.preview || null;
    },
    archivePackage(packageId, key) {
      return client.post(`${CATALOG_ROOT}/packages/${enc(packageId)}/archive`, { reason: 'operator_archived' }, withKey(key));
    },
    unarchivePackage(packageId, { versionId, videoArtifactId, posterArtifactId }, key) {
      return client.post(`${CATALOG_ROOT}/packages/${enc(packageId)}/unarchive`, { versionId, videoArtifactId, posterArtifactId }, withKey(key));
    },
    async deletePackage(packageId, key) {
      return (await client.del(`${CATALOG_ROOT}/packages/${enc(packageId)}`, withKey(key)))?.deleted || null;
    },
    async cleanUpPackageStorage(packageId, key) {
      return (await client.post(`${CATALOG_ROOT}/packages/${enc(packageId)}/storage-cleanup`, {}, withKey(key)))?.cleanup || null;
    },
    createPackage(body, key) {
      return client.post(`${CATALOG_ROOT}/packages`, body, withKey(key));
    },
    updatePackageDetails(packageId, body, key) {
      return client.patch(`${CATALOG_ROOT}/packages/${enc(packageId)}`, body, withKey(key));
    },
    reassignPackageType(packageId, renovationTypeId, key) {
      return client.put(`${CATALOG_ROOT}/packages/${enc(packageId)}/renovation-type`, { renovationTypeId }, withKey(key));
    },
    uploadAsset(packageId, versionId, assetKind, file, headers) {
      return client.upload(`${CATALOG_ROOT}/packages/${enc(packageId)}/versions/${enc(versionId)}/assets/${assetKind}`, file, { headers });
    },
    async copyAsset(packageId, versionId, assetKind, { sourceVersionId, artifactId }, key) {
      return (await client.post(`${CATALOG_ROOT}/packages/${enc(packageId)}/versions/${enc(versionId)}/assets/${assetKind}/copy`, { sourceVersionId, artifactId }, withKey(key)))?.asset || null;
    },
    async createVersion(packageId, body, key) {
      return (await client.post(`${CATALOG_ROOT}/packages/${enc(packageId)}/versions`, body, withKey(key)))?.version || null;
    },
    publishPackage(packageId, versionId, key) {
      return client.post(`${CATALOG_ROOT}/packages/${enc(packageId)}/publish`, { versionId }, withKey(key));
    },
    async typeUsage() {
      const response = await client.get(`${CATALOG_ROOT}/renovation-types/usage`);
      return Array.isArray(response?.renovationTypes) ? response.renovationTypes : [];
    },
    createType({ id, key, displayName, displayOrder }, idempotencyKey) {
      return client.post(`${CATALOG_ROOT}/renovation-types`, { id, key, displayName, displayOrder }, withKey(idempotencyKey));
    },
    renameType(renovationTypeId, displayName, key) {
      return client.patch(`${CATALOG_ROOT}/renovation-types/${enc(renovationTypeId)}`, { displayName }, withKey(key));
    },
    reorderTypes(renovationTypeIds, expectedRenovationTypeIds, key) {
      return client.put(`${CATALOG_ROOT}/renovation-types/order`, { renovationTypeIds, expectedRenovationTypeIds }, withKey(key));
    },
    archiveType(renovationTypeId, key) {
      return client.post(`${CATALOG_ROOT}/renovation-types/${enc(renovationTypeId)}/archive`, {}, withKey(key));
    },
    unarchiveType(renovationTypeId, key) {
      return client.post(`${CATALOG_ROOT}/renovation-types/${enc(renovationTypeId)}/unarchive`, {}, withKey(key));
    },
    deleteType(renovationTypeId, key) {
      return client.del(`${CATALOG_ROOT}/renovation-types/${enc(renovationTypeId)}`, withKey(key));
    },
  };
}

// A renovation-type key from its name: lowercase words joined by "_"
// (the database key format), with a short suffix when it is taken.
export function renovationTypeKey(name, takenKeys = []) {
  const base = String(name || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
    .replace(/_+$/, '') || 'renovation';
  const taken = new Set(takenKeys);
  if (!taken.has(base)) return base;
  for (let suffix = 2; suffix < 1000; suffix += 1) {
    if (!taken.has(`${base}_${suffix}`)) return `${base}_${suffix}`;
  }
  return `${base}_${makeUuid().slice(0, 8)}`;
}

export function sameName(left, right) {
  return String(left || '').trim().toLocaleLowerCase() === String(right || '').trim().toLocaleLowerCase();
}

const MESSAGES = {
  catalog_renovation_type_in_use: 'This type is still used by an active creative or saved draft.',
  catalog_renovation_type_referenced: 'This type is referenced by creatives, their saved history or campaigns.',
  catalog_renovation_type_archived: 'This renovation type is archived. Unarchive it first.',
  catalog_renovation_type_order_stale: 'Another change updated the type order. The latest order has been loaded; try again.',
  catalog_renovation_type_busy: 'Another catalog change is in progress. Try again in a moment.',
  catalog_renovation_type_not_found: 'This renovation type no longer exists. The list has been refreshed.',
  catalog_renovation_type_name_taken: 'A renovation type with this name already exists.',
  catalog_package_not_found: 'This creative no longer exists. The list has been refreshed.',
  catalog_package_archived: 'This creative is archived.',
  catalog_package_not_archived: 'This creative is no longer archived. The list has been refreshed.',
  catalog_package_in_use: 'This creative is used by campaigns.',
  catalog_package_not_deletable: 'Only unused, never-published drafts can be permanently deleted.',
  catalog_package_version_not_found: 'A saved version of this creative could not be found. Refresh and try again.',
  catalog_asset_copy_target_exists: 'Saved media could not be copied safely. Try again.',
  catalog_asset_copy_verification_failed: 'Saved media could not be verified after copying. Try again.',
  catalog_asset_checksum_duplicate: 'This file is already saved in the catalog. Keep the saved media or choose a different file.',
  catalog_package_key_taken: 'Another creative already uses this creative key. Try saving again.',
  catalog_version_idempotency_conflict: 'An earlier save finished while the connection was interrupted. It has been loaded; save again to keep these changes.',
};

export function catalogErrorMessage(error, fallback = 'The catalog change could not be completed. Try again.') {
  const code = error?.code || error?.body?.error;
  if (MESSAGES[code]) return MESSAGES[code];
  if (Number(error?.status) === 403) return 'Your platform account cannot manage the creative catalog.';
  return fallback;
}

export function blockedReasons(error) {
  const reasons = error?.blockedReasons || error?.body?.blockedReasons;
  return Array.isArray(reasons) ? reasons : [];
}
